import { InformationCircleIcon } from '@heroicons/react/24/outline';
import {
  Copy01Icon,
  DotsVerticalIcon,
  Download01Icon,
  Image01Icon,
  Pencil01Icon,
  Trash04Icon,
} from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';
import Alert from '@mui/joy/Alert';
import Box from '@mui/joy/Box';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { mutate } from 'swr';
import { useDebounce } from 'use-debounce';

import { CommandItem } from '@/components/Command';
import { ComparativeExperimentsTable } from '@/components/ComparativeExperiments/ComparativeExperimentsTable';
import { DatasetCrudPane } from '@/components/DatasetCrudPane/DatasetCrudPane';
import { DatasetExportPane } from '@/components/DatasetExportPane/DatasetExportPane';
import { DeleteModal } from '@/components/Delete';
import { ErrorBanner } from '@/components/ErrorBanner';
import { DatasetEvaluatorsTableV2 } from '@/components/Evaluators/DatasetEvaluatorsTableV2';
import { DatasetEvaluatorsTable } from '@/components/Evaluators/EvaluatorsTable';
import { ExamplesTable } from '@/components/ExamplesTable';
import { IFrameTraceRenderingModal } from '@/components/IFrameTraceRenderingModal';
import {
  PopoverDropdownMenu,
  PopoverDropdownMenuItem,
} from '@/components/PopoverDropdownMenu';
import { ResourceDetailLayout } from '@/components/ResourceDetailTabLayout/ResourceDetailTabLayout';
import {
  ResourceActionsSlot,
  ResourceTabsSlot,
} from '@/components/ResourceDetailTabLayout/ResourceTabLayoutPortals';
import { SessionsTable } from '@/components/SessionsTable';
import { TabGroup } from '@/components/Tabs/Tabs';
import { useSetTab, useTab } from '@/components/Tabs/useTab';
import useToast from '@/components/Toast/useToast';
import { FetcherParams } from '@/data/fetcher';
import {
  IconButton,
  TabLabel as TabLabelV2,
  TabList as TabListV2,
  TabPanel as TabPanelV2,
  TabPanels as TabPanelsV2,
} from '@/design-system';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { usePermissions } from '@/hooks/usePermissions.tsx';
import { usePublicSharingDisabled } from '@/hooks/usePublicSharingDisabled';
import { useSegmentEventWrapper } from '@/hooks/useSegmentEventWrapper';
import {
  useCloneDatasetMutation,
  useDataset,
  useDatasetSingleVersion,
  useDatasetVersionsDiff,
  useDatasetVersionsInfinite,
  useOrganizationId,
} from '@/hooks/useSwr';
import { useAlignmentStore } from '@/stores/useAlignmentStore';
import { DatasetVersionsDiffSchema, ResourceType } from '@/types/schema';
import { getDatasetPath } from '@/utils';
import { addQueryParams } from '@/utils/add-query-params';
import {
  apiDatasetsPath,
  apiExamplesPath,
  appComparePath,
  appDatasetsPath,
  appOrganizationPath,
} from '@/utils/constants';
import { isSelfHosted } from '@/utils/is-self-hosted';
import { cn } from '@/utils/tailwind';

import { DatasetRulesDropdown } from '../../components/EvaluatorPopover/EvaluatorsPopover';
import { DatasetRuleButton } from './DatasetRulesPane';
import { CloneDatasetExamplesModal } from './components/CloneDatasetExamplesModal';
import { CreateEvaluatorFromExperimentDataDialog } from './components/CreateEvaluatorFromExperimentDataDialog';
import { DatasetVersionDropdownMenu } from './components/DatasetVersionDropdownMenu';
import { FewShotSearchPanel } from './components/FewShotSearchPanel';
import { NewExperimentButton } from './components/NewExperimentButton';
import { ShareDatasetAction } from './components/ShareDatasetAction';
import {
  DATASET_TABS,
  DatasetTab,
  TITLE_ALL_VERSIONS,
  getTabIndex,
} from './constants';
import { GroupedDatasetVersionSchemaWithOptionalAsOf } from './types';

export function NoProjects() {
  return (
    <Box marginBottom={3}>
      <Alert variant="outlined">There are no experiments yet.</Alert>
    </Box>
  );
}

const VERSIONS_PAGE_SIZE = 20;

const DatasetV2 = () => {
  const { datasetId } = useParams();
  const dataset = useDataset(datasetId!);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isDatasetCrudPaneOpen, setIsDatasetCrudPaneOpen] = useState(false);
  const [isCloneDatasetModalOpen, setIsCloneDatasetModalOpen] = useState(false);
  const [
    isCustomIFrameRenderingModalOpen,
    setIsCustomIFrameRenderingModalOpen,
  ] = useState(false);
  const [selectedExamples, setSelectedExamples] = useState({});

  const { value: allowCustomIFrames } = useOrgConfig(
    OrgConfigs.allow_custom_iframes
  );
  const { value: formulasEnabled } = useOrgConfig(
    OrgConfigs.feedback_formulas_enabled
  );

  const organizationId = useOrganizationId();
  const toPreserve = ['as_of'];
  const setTab = useSetTab({ preserve: toPreserve });
  const tab = useTab();
  const [defaultVersions, setDefaultVersions] = useState<
    GroupedDatasetVersionSchemaWithOptionalAsOf[]
  >([]);
  const [cloneTargetDatasetId, setCloneTargetDatasetId] = useState<
    string | undefined
  >();

  const [searchVersion, setSearchVersion] = useState<string>('');
  const [searchVersionDebounced] = useDebounce(searchVersion, 250);
  const {
    data: versionPages,
    size,
    setSize,
    mutate: mutateVersionsSWR,
    isLoading: versionPagesLoading,
  } = useDatasetVersionsInfinite(VERSIONS_PAGE_SIZE, datasetId ?? null, {
    search: searchVersionDebounced,
  });

  const [isDeleteDatasetModalOpen, setIsDeleteDatasetModalOpen] =
    useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsAsOf = searchParams.get('as_of');
  const decodedSearchParamsAsOf = searchParamsAsOf
    ? decodeURIComponent(searchParamsAsOf)
    : null;
  const versionsFlat = useMemo(
    () => versionPages?.flat() ?? [],
    [versionPages]
  );

  // Alignment mode parameters
  const alignmentMode = useAlignmentStore((state) => state.alignmentMode);
  const alignmentQueueId = useAlignmentStore((state) => state.alignmentQueueId);
  const runRuleId = useAlignmentStore((state) => state.runRuleId);
  const setAlignmentMode = useAlignmentStore((state) => state.setAlignmentMode);
  useEffect(() => {
    return () => setAlignmentMode(false);
  }, [setAlignmentMode, tab]);

  const [
    isCreatingEvaluatorFromExperimentData,
    setIsCreatingEvaluatorFromExperimentData,
  ] = useState(false);

  const handStartAlignEvaluator = () => {
    setIsCreatingEvaluatorFromExperimentData(true);
    setIsCreateEvaluatorDialogOpen(true);
    setTab(getTabIndex('experiments'));
  };

  // Track CreateEvaluatorFromExperimentDataDialog state
  const [isCreateEvaluatorDialogOpen, setIsCreateEvaluatorDialogOpen] =
    useState(searchParams.get('showCreateEvaluatorModal') === 'true');

  const mutateVersions = useCallback(() => {
    setSearchVersion('');
    mutateVersionsSWR();
    mutate((key: FetcherParams) => key?.url?.startsWith(apiDatasetsPath));
  }, [mutateVersionsSWR, setSearchVersion]);

  const selectedVersionSWR = useDatasetSingleVersion(
    datasetId ?? null,
    decodedSearchParamsAsOf ? { as_of: decodedSearchParamsAsOf } : null
  );

  const selectedVersion = decodedSearchParamsAsOf
    ? selectedVersionSWR.data
    : null;
  const setSelectedVersion = useCallback(
    (version: GroupedDatasetVersionSchemaWithOptionalAsOf | null) => {
      const isLatestVersion = (version?.tags ?? []).includes('latest');
      setSearchParams(
        (prev) => {
          if (
            version?.as_of &&
            !(isLatestVersion && tab === getTabIndex('examples'))
          ) {
            prev.set('as_of', version.as_of);
          } else {
            prev.delete('as_of');
          }
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams, tab]
  );

  const examplesMutated = useCallback(() => {
    // if an example was changed, we need to reset the selectedVersion as well
    mutate((key: FetcherParams) => key?.url?.startsWith(apiExamplesPath));
    setSelectedVersion(null);
    mutateVersions();
  }, [mutateVersions, setSelectedVersion]);
  const { createToast } = useToast();
  const navigate = useNavigate();

  const cloneDataset = useCloneDatasetMutation({
    onSuccess: () => {
      setIsCloneDatasetModalOpen(false);
      if (cloneTargetDatasetId) {
        createToast({
          title: 'Examples Copied Successfully',
          description: (
            <Link
              to={`/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${cloneTargetDatasetId}`}
              className="underline"
            >
              View the dataset
            </Link>
          ),
        });
      }
    },
    onError: (e) => {
      createToast({
        title: 'Error copying examples',
        description: e.message,
        error: true,
      });
    },
  });

  useEffect(() => {
    if (versionsFlat.length > 0 && searchVersion === '') {
      setDefaultVersions([
        { tags: [TITLE_ALL_VERSIONS], as_of: undefined },
        versionsFlat[0],
      ]);
    }
  }, [searchVersion, versionsFlat]);

  const showAllVersionsOption = tab === 0 && searchVersion === '';
  const versionsToDisplay = showAllVersionsOption
    ? [{ tags: [TITLE_ALL_VERSIONS], as_of: undefined }, ...versionsFlat]
    : versionsFlat;
  const selectedVersionToUse = selectedVersion ?? defaultVersions[tab];

  const pageLoadEffectDone = useRef(false);
  useEffect(() => {
    if (pageLoadEffectDone.current) {
      return;
    }

    if (dataset.data) {
      if (!dataset.data.session_count) {
        setTab(getTabIndex('examples'));
      }
      pageLoadEffectDone.current = true;
    }
  }, [dataset.data, setTab]);

  const currentVersionDiff = useDatasetVersionsDiff(
    datasetId ?? null,
    tab === getTabIndex('examples') &&
      !selectedVersionToUse?.tags?.includes('latest') &&
      selectedVersionToUse?.as_of
      ? {
          from_version: selectedVersionToUse?.as_of,
          to_version: 'latest',
        }
      : null
  );

  const exampleCount = useMemo(() => {
    if (!decodedSearchParamsAsOf) {
      return dataset.data?.example_count;
    } else {
      return undefined;
    }
  }, [decodedSearchParamsAsOf, dataset.data?.example_count]);

  const { authorize, isLoading: isPermsLoading } = usePermissions();

  const datasetVersioningComponent = datasetId && (
    <div className="flex gap-2 self-end">
      <DatasetVersionDropdownMenu
        selectedVersion={selectedVersionToUse}
        datasetId={datasetId}
        setSelectedVersion={setSelectedVersion}
        versions={versionsToDisplay}
        size={size}
        setSize={setSize}
        isLoadingVersions={versionPagesLoading}
        totalVersions={
          Number(
            versionPages?.[versionPages.length - 1]?.headers?.[
              'x-pagination-total'
            ] ?? 0
          ) + (showAllVersionsOption ? 1 : 0)
        }
        setSearch={setSearchVersion}
        search={searchVersion}
        isLoadingCurrentVersion={selectedVersionSWR.isLoading}
        versionPageSize={VERSIONS_PAGE_SIZE}
        mutateVersions={mutateVersions}
      />
    </div>
  );

  const isReadOnly =
    decodedSearchParamsAsOf !== null &&
    decodedSearchParamsAsOf !== defaultVersions[1]?.as_of;

  const publicSharingDisabled = usePublicSharingDisabled();
  const threeDotMenuOptions = [
    authorize('datasets:share') &&
      dataset.data && {
        label: 'Share Dataset',
        customCell: (
          <ShareDatasetAction
            variant="custom"
            dataset={dataset.data}
            bypassPortal
            customButton={(icon, isPublic) => (
              <div>
                <CommandItem
                  className={cn(
                    'mx-1.5 flex cursor-pointer flex-col items-center px-0 py-0'
                  )}
                  disabled={publicSharingDisabled}
                >
                  <PopoverDropdownMenuItem
                    icon={<div className="flex items-center">{icon}</div>}
                    title={
                      <div className="text-md">
                        {isPublic ? 'Public Dataset' : 'Share Dataset'}
                      </div>
                    }
                    selected={false}
                    titleBold
                  />
                </CommandItem>
              </div>
            )}
          />
        ),
      },
    {
      label: 'Download Dataset',
      icon: <Download01Icon className="h-4 w-4" />,
      onClick: () => setIsExportOpen(true),
    },
    authorize('datasets:update') && {
      label: 'Edit Dataset',
      icon: <Pencil01Icon className="h-4 w-4" />,
      onClick: () => setIsDatasetCrudPaneOpen(true),
      dataTestId: 'edit-dataset-menu-item',
    },
    authorize('datasets:update') && {
      label: 'Clone Dataset',
      icon: <Copy01Icon className="h-4 w-4" />,
      onClick: () => setIsCloneDatasetModalOpen(true),
    },
    allowCustomIFrames && {
      label: 'Custom Trace Rendering',
      icon: <Image01Icon className="h-4 w-4" />,
      onClick: () => setIsCustomIFrameRenderingModalOpen(true),
    },
    authorize('datasets:delete') && {
      label: 'Delete Dataset',
      icon: <Trash04Icon className="h-4 w-4" />,
      onClick: () => setIsDeleteDatasetModalOpen(true),
    },
  ].filter(Boolean) as {
    label: string;
    icon?: JSX.Element;
    onClick?: () => void;
    customCell?: JSX.Element;
    dataTestId?: string;
  }[];

  const tabsToShow = DATASET_TABS.filter(
    (tab) => !isSelfHosted || tab.showForSelfHosted
  );

  const tabPanels: { [key in DatasetTab['id']]: JSX.Element } = {
    experiments: (
      <TabPanelV2>
        <Box>
          {selectedVersion?.as_of && (
            <ViewingSingleVersionBanner
              onClick={() => {
                setSelectedVersion(null);
              }}
              bodyText="Viewing experiments from a single version."
              linkText="Click here to view all experiments"
            />
          )}
          <SessionsTable
            filter={{
              reference_dataset: [datasetId!],
              ...(decodedSearchParamsAsOf
                ? { dataset_version: decodedSearchParamsAsOf }
                : {}),
            }}
            emptyState={<NoProjects />}
            columnVisibility={{
              run_count: true,
              total_tokens: false,
              total_cost: false,
            }}
            isDatasetPage={true}
            getCompareClickUrl={
              organizationId
                ? (rowSelection) => {
                    const basePath = getDatasetPath(datasetId!, organizationId);
                    return addQueryParams(`${basePath}/${appComparePath}`, {
                      selectedSessions: rowSelection.join(','),
                    });
                  }
                : undefined
            }
            localStorageKey="dataset-sessions-table"
            alignmentMode={alignmentMode}
            setAlignmentMode={setAlignmentMode}
            alignmentQueueId={alignmentQueueId}
            runRuleId={runRuleId ?? undefined}
          />
        </Box>
      </TabPanelV2>
    ),
    examples: (
      <TabPanelV2>
        {isReadOnly && (
          <>
            <ViewingSingleVersionBanner
              onClick={() => {
                setSelectedVersion(null);
              }}
              bodyText="Examples are read-only when viewing a past version of a dataset."
              linkText="Click here to view the latest version."
            />
            {currentVersionDiff.data && (
              <DatasetVersionsDiffComponent
                currentVersionDiff={currentVersionDiff.data}
              />
            )}
          </>
        )}
        <ExamplesTable
          rowSelection={selectedExamples}
          setRowSelection={setSelectedExamples}
          filter={{
            dataset: datasetId,
            ...(decodedSearchParamsAsOf
              ? { as_of: decodedSearchParamsAsOf }
              : {}),
          }}
          onMutate={examplesMutated}
          isReadOnly={isReadOnly}
          dataType={dataset.data?.data_type}
          dataset={dataset.data}
          totalExamplesCount={exampleCount}
        />
      </TabPanelV2>
    ),
    evaluators: (
      <TabPanelV2>
        {formulasEnabled ? (
          <DatasetEvaluatorsTableV2
            onStartAlignEvaluator={handStartAlignEvaluator}
          />
        ) : (
          <DatasetEvaluatorsTable
            onStartAlignEvaluator={handStartAlignEvaluator}
          />
        )}
      </TabPanelV2>
    ),
    'pairwise-experiments': (
      <TabPanelV2>
        <ComparativeExperimentsTable datasetId={datasetId!} />
      </TabPanelV2>
    ),
    'few-shot-search': (
      <TabPanelV2>
        <FewShotSearchPanel
          datasetId={datasetId ?? ''}
          inputSchema={dataset.data?.inputs_schema_definition}
          onMutate={examplesMutated}
          setEditDatasetOpen={() => setIsDatasetCrudPaneOpen(true)}
        />
      </TabPanelV2>
    ),
  };
  const track = useSegmentEventWrapper((index: number) => {
    const tabName = Object.keys(tabPanels)[index];
    return [
      'dataset_tab_change',
      {
        tab_name: tabName,
        location: 'dataset_page',
      },
    ];
  });
  const trackTabChange = track(() => void 0);

  const pageTitleProps = useMemo(
    () => ({
      title: dataset.data?.name ?? '',
      id: datasetId ?? '',
      type: ResourceType.Dataset,
      description: dataset.data?.description,
      isLoading: dataset.isLoading,
    }),
    [
      dataset.data?.name,
      datasetId,
      dataset.data?.description,
      dataset.isLoading,
    ]
  );

  if (isPermsLoading) {
    return <LinearProgress />;
  }

  if (!authorize('datasets:read')) {
    return <ErrorBanner className="m-4">Forbidden</ErrorBanner>;
  }

  return (
    <ResourceDetailLayout pageTitleProps={pageTitleProps}>
      <TabGroup preserve={toPreserve} onChange={trackTabChange}>
        <ResourceTabsSlot.Fill>
          <TabListV2>
            {tabsToShow.map((tab) => (
              <TabLabelV2 key={tab.id} label={tab.tabName} />
            ))}
          </TabListV2>
        </ResourceTabsSlot.Fill>
        <TabPanelsV2 className="flex flex-grow flex-col overflow-hidden overflow-y-auto p-4">
          {tabsToShow.map((tab) => tabPanels[tab.id])}
        </TabPanelsV2>
      </TabGroup>

      {dataset.data && (
        <ResourceActionsSlot.Fill>
          <div className="flex items-center gap-2">
            {tabsToShow[tab]?.showVersioning && datasetVersioningComponent}

            {datasetId && authorize('rules:read') && (
              <DatasetRulesDropdown
                trigger={
                  <DatasetRuleButton
                    datasetId={datasetId}
                    onClick={() => void 0}
                    variant="dataset"
                  />
                }
                datasetId={datasetId}
                checkable={false}
                hideCurrentRules={true}
                onStartAlignEvaluator={handStartAlignEvaluator}
              />
            )}

            {datasetId && <NewExperimentButton datasetId={datasetId} />}

            {threeDotMenuOptions.length > 0 && (
              <PopoverDropdownMenu
                values={threeDotMenuOptions}
                onSelect={(option) => option.onClick?.()}
                getTitle={(option) => option.label}
                getCustomCell={(option) => option.customCell}
                getIsSelected={() => false}
                getIcon={(option) => option.icon}
                getDataTestId={(option) => option.dataTestId || ''}
                asChild
              >
                <IconButton
                  size="md"
                  label="More"
                  variant="plain"
                  color="secondary"
                  data-testid="dataset-more-button"
                  icon={DotsVerticalIcon}
                />
              </PopoverDropdownMenu>
            )}
          </div>
        </ResourceActionsSlot.Fill>
      )}

      {isExportOpen && (
        <DatasetExportPane
          open={true}
          onCancel={() => setIsExportOpen(false)}
          onSubmit={() => setIsExportOpen(false)}
          defaultDatasetId={datasetId!}
          asOf={selectedVersionToUse?.as_of}
        />
      )}

      {isCloneDatasetModalOpen && datasetId != null && (
        <CloneDatasetExamplesModal
          open={true}
          onClose={() => setIsCloneDatasetModalOpen(false)}
          prefilledData={{
            inputs_schema_definition: dataset.data?.inputs_schema_definition,
            outputs_schema_definition: dataset.data?.outputs_schema_definition,
          }}
          sourceDatasetId={datasetId ?? null}
          sourceDataType={dataset.data?.data_type}
          targetDatasetId={cloneTargetDatasetId}
          isLoading={cloneDataset.isMutating}
          setTargetDatasetId={setCloneTargetDatasetId}
          onSubmit={() => {
            if (cloneTargetDatasetId) {
              cloneDataset.trigger({
                json: {
                  source_dataset_id: datasetId,
                  target_dataset_id: cloneTargetDatasetId,
                  as_of: selectedVersionToUse?.as_of,
                },
              });
            }
          }}
        />
      )}
      {isCustomIFrameRenderingModalOpen && datasetId != null && (
        <IFrameTraceRenderingModal
          datasetId={datasetId}
          open={true}
          onClose={() => setIsCustomIFrameRenderingModalOpen(false)}
        />
      )}
      {isDatasetCrudPaneOpen && (
        <DatasetCrudPane
          open={true}
          onCancel={() => setIsDatasetCrudPaneOpen(false)}
          dataset={dataset.data}
          onSuccess={() => dataset.mutate()}
          label="Edit"
        />
      )}
      {isDeleteDatasetModalOpen && (
        <DeleteModal
          endpoint={`${apiDatasetsPath}`}
          id={dataset.data?.id}
          name={dataset.data?.name}
          isOpen={true}
          doClose={() => setIsDeleteDatasetModalOpen(false)}
          invalidationPrefixes={['/datasets']}
          onSuccess={() => {
            navigate(
              `/${appOrganizationPath}/${organizationId}/${appDatasetsPath}`
            );
          }}
        />
      )}
      <CreateEvaluatorFromExperimentDataDialog
        open={isCreateEvaluatorDialogOpen}
        isCreating={isCreatingEvaluatorFromExperimentData}
        datasetId={datasetId}
        onOpenChange={(open) => {
          setIsCreateEvaluatorDialogOpen(open);
          setIsCreatingEvaluatorFromExperimentData(open);
        }}
      />
    </ResourceDetailLayout>
  );
};

function ViewingSingleVersionBanner(props: {
  onClick: () => void;
  bodyText: string;
  linkText: string;
}) {
  return (
    <div className="mb-4 rounded-xl bg-secondary px-5 py-4 pl-8">
      <div className="flex items-center gap-6">
        <div className="relative h-5 w-5 rounded-[20px]">
          <div className="absolute left-[-4px] top-[-4px] h-7 w-7 rounded-3xl border-2 border-brand opacity-30" />
          <div className="absolute left-[-9px] top-[-9px] h-[38px] w-[38px] rounded-3xl border-2 border-brand opacity-10" />
          <div className="absolute left-0 top-0 h-5 w-5" />
          <InformationCircleIcon className="absolute left-0 top-0 h-5 w-5 stroke-brand-green-400" />
        </div>
        <div className="flex items-start gap-2">
          <span>{props.bodyText}</span>
          <a
            onClick={props.onClick}
            className="cursor-pointer text-brand-green-600 hover:underline"
          >
            {props.linkText}
          </a>
        </div>
      </div>
    </div>
  );
}

function DatasetVersionsDiffComponent(props: {
  currentVersionDiff: DatasetVersionsDiffSchema;
}) {
  return (
    <div className="mb-3 ml-2 flex gap-1.5">
      <div className="text-success-primary">
        {props.currentVersionDiff.examples_added.length}{' '}
        {props.currentVersionDiff.examples_added.length !== 1
          ? 'additions'
          : 'addition'}
        ,{' '}
      </div>
      <div className="text-brand-green-400">
        {props.currentVersionDiff.examples_modified.length}{' '}
        {props.currentVersionDiff.examples_modified.length !== 1
          ? 'modifications'
          : 'modification'}
        ,{' '}
      </div>
      <div className="text-error">
        {props.currentVersionDiff.examples_removed.length}{' '}
        {props.currentVersionDiff.examples_removed.length !== 1
          ? 'deletions'
          : 'deletion'}
      </div>
      between this version and the latest version.
    </div>
  );
}

const Dataset = () => {
  const { datasetId } = useParams();

  return datasetId ? <DatasetV2 /> : null;
};

export default Dataset;
